import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sprout, Plus, Search, Calendar } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { CardGridSkeleton } from "@/components/LoadingSkeleton";
import { authFetch } from "@/lib/auth";
import { toast } from "sonner";

interface Planting {
  id: string;
  ranch_id: string;
  crop_variety_id: string;
  growing_template_id: string;
  pasture_id: string;
  field_subdivision_id: string | null;
  sow_date: string;
  transplant_date: string | null;
  expected_harvest_date: string | null;
  actual_harvest_date: string | null;
  status: "planned" | "sown" | "growing" | "harvesting" | "complete" | "failed";
  quantity_planted: number | null;
  quantity_unit: string | null;
  notes: string | null;
}

interface PlannerItem extends Planting {
  variety_name: string;
  crop_name: string;
  crop_category: string;
  pasture_name: string;
  field_name: string | null;
}

interface Crop {
  id: string;
  ranch_id: string | null;
  name: string;
  is_system: number;
}

interface CropVariety {
  id: string;
  name: string;
  days_to_maturity: number | null;
}

interface GrowingTemplate {
  id: string;
  name: string;
  cultivation_method: "direct_seed" | "transplant" | "both";
  start_indoor_days_before: number | null;
  ranch_id: string | null;
  is_system: number;
}

interface GrowingTemplateInput {
  id: string;
  inventory_item_id: string | null;
  item_name: string;
  unit: string;
}

interface Pasture {
  id: string;
  name: string;
}

interface FieldSubdivision {
  id: string;
  pasture_id: string;
  name: string;
}

interface InventoryItem {
  id: string;
  name: string;
  unit: string;
}

const STATUS_STYLES: Record<Planting["status"], string> = {
  planned: "bg-token-muted/15 text-token-muted border-token-muted/30 dark:bg-token-muted-dark/20 dark:text-token-muted-dark dark:border-token-muted-dark/30",
  sown: "bg-burnt-orange/15 text-burnt-orange border-burnt-orange/30 dark:bg-burnt-orange-dark/20 dark:text-burnt-orange-dark dark:border-burnt-orange-dark/30",
  growing: "bg-olive/15 text-olive border-olive/30 dark:bg-olive-dark/20 dark:text-olive-dark dark:border-olive-dark/30",
  harvesting: "bg-burnt-orange/15 text-burnt-orange border-burnt-orange/30 dark:bg-burnt-orange-dark/20 dark:text-burnt-orange-dark dark:border-burnt-orange-dark/30",
  complete: "bg-moss/15 text-moss border-moss/30 dark:bg-moss-dark/20 dark:text-moss-dark dark:border-moss-dark/30",
  failed: "bg-clay/15 text-clay border-clay/30 dark:bg-clay-dark/20 dark:text-clay-dark dark:border-clay-dark/30",
};

const STATUS_LABELS: Record<Planting["status"], string> = {
  planned: "Planned",
  sown: "Sown",
  growing: "Growing",
  harvesting: "Harvesting",
  complete: "Complete",
  failed: "Failed",
};

export function PlantingsPage() {
  const [plantings, setPlantings] = useState<PlannerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Create dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [crops, setCrops] = useState<Crop[]>([]);
  const [varieties, setVarieties] = useState<CropVariety[]>([]);
  const [templates, setTemplates] = useState<GrowingTemplate[]>([]);
  const [templateInputs, setTemplateInputs] = useState<GrowingTemplateInput[]>([]);
  const [pastures, setPastures] = useState<Pasture[]>([]);
  const [fields, setFields] = useState<FieldSubdivision[]>([]);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);

  const [formCrop, setFormCrop] = useState("");
  const [formVariety, setFormVariety] = useState("");
  const [formTemplate, setFormTemplate] = useState("");
  const [formPasture, setFormPasture] = useState("");
  const [formField, setFormField] = useState("");
  const [formSowDate, setFormSowDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [formQuantity, setFormQuantity] = useState("");
  const [formQuantityUnit, setFormQuantityUnit] = useState<"seeds" | "transplants" | "lbs" | "plants">("seeds");
  // bindings: input.id -> inventory_item.id
  const [bindings, setBindings] = useState<Record<string, string>>({});

  const fetchPlantings = async () => {
    try {
      const data = await authFetch<{ items: PlannerItem[] }>("/api/plantings/planner");
      setPlantings(data.items ?? []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to load plantings";
      setError(msg);
      console.error("[Plantings]", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlantings();
  }, []);

  // Lazy-load form data on first dialog open
  useEffect(() => {
    if (!dialogOpen || crops.length > 0) return;
    (async () => {
      try {
        const [cropsResp, pasturesResp, invResp] = await Promise.all([
          authFetch<{ items: Crop[] }>("/api/crops"),
          authFetch<{ items: Pasture[] }>("/api/pastures"),
          authFetch<{ items: InventoryItem[] }>("/api/inventory"),
        ]);
        // Plantings need a ranch-owned crop (cloned from system) — system crops
        // can't be planted directly because their growing-template inputs have
        // NULL inventory_item_id.
        setCrops((cropsResp.items ?? []).filter((c) => c.is_system === 0));
        setPastures(pasturesResp.items ?? []);
        setInventory(invResp.items ?? []);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Failed to load form data";
        toast.error(msg);
      }
    })();
  }, [dialogOpen, crops.length]);

  // Cascade: when crop changes, load its varieties
  useEffect(() => {
    if (!formCrop) {
      setVarieties([]);
      setFormVariety("");
      return;
    }
    (async () => {
      try {
        const data = await authFetch<{ items: CropVariety[] }>(
          `/api/crops/${formCrop}/varieties`
        );
        setVarieties(data.items ?? []);
        if (data.items && data.items.length > 0) {
          setFormVariety(data.items[0].id);
        } else {
          setFormVariety("");
        }
      } catch (err) {
        console.error("[Varieties]", err);
      }
    })();
  }, [formCrop]);

  // Cascade: when variety changes, load growing templates for it
  useEffect(() => {
    if (!formVariety) {
      setTemplates([]);
      setFormTemplate("");
      return;
    }
    (async () => {
      try {
        const data = await authFetch<{ items: GrowingTemplate[] }>(
          `/api/growing-templates?crop_variety_id=${formVariety}&include_system=false`
        );
        setTemplates(data.items ?? []);
        if (data.items && data.items.length > 0) {
          setFormTemplate(data.items[0].id);
        } else {
          setFormTemplate("");
        }
      } catch (err) {
        console.error("[Templates]", err);
      }
    })();
  }, [formVariety]);

  // Cascade: when template changes, load its inputs to set up bindings
  useEffect(() => {
    if (!formTemplate) {
      setTemplateInputs([]);
      setBindings({});
      return;
    }
    (async () => {
      try {
        const data = await authFetch<{
          data: { template: GrowingTemplate; tasks: unknown[]; inputs: GrowingTemplateInput[] };
        }>(`/api/growing-templates/${formTemplate}`);
        // authFetch unwraps `data` already, so data here is the inner shape
        const inputs = (data as unknown as { template: GrowingTemplate; inputs: GrowingTemplateInput[] }).inputs ?? [];
        setTemplateInputs(inputs);
        // Reset bindings — pre-populate any pre-bound inputs to themselves
        const preBindings: Record<string, string> = {};
        for (const i of inputs) {
          if (i.inventory_item_id) preBindings[i.id] = i.inventory_item_id;
        }
        setBindings(preBindings);
      } catch (err) {
        console.error("[TemplateDetail]", err);
      }
    })();
  }, [formTemplate]);

  // Cascade: when pasture changes, load its field subdivisions
  useEffect(() => {
    if (!formPasture) {
      setFields([]);
      setFormField("");
      return;
    }
    (async () => {
      try {
        const data = await authFetch<{ items: FieldSubdivision[] }>(
          `/api/pastures/${formPasture}/fields`
        );
        setFields(data.items ?? []);
      } catch (err) {
        console.error("[Fields]", err);
      }
    })();
  }, [formPasture]);

  const filtered = useMemo(() => {
    return plantings.filter((p) => {
      const haystack = [p.crop_name, p.variety_name, p.pasture_name, p.field_name]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchSearch = haystack.includes(search.toLowerCase());
      const matchStatus = statusFilter === "all" || p.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [plantings, search, statusFilter]);

  const resetForm = () => {
    setFormCrop("");
    setFormVariety("");
    setFormTemplate("");
    setFormPasture("");
    setFormField("");
    setFormSowDate(new Date().toISOString().split("T")[0]);
    setFormQuantity("");
    setFormQuantityUnit("seeds");
    setBindings({});
  };

  const unboundInputs = templateInputs.filter(
    (inp) => !inp.inventory_item_id && !bindings[inp.id]
  );

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formVariety || !formTemplate || !formPasture) {
      toast.error("Crop variety, growing template, and pasture are required");
      return;
    }
    if (unboundInputs.length > 0) {
      toast.error(`Bind inventory for: ${unboundInputs.map((i) => i.item_name).join(", ")}`);
      return;
    }
    setSubmitting(true);
    try {
      await authFetch("/api/plantings", {
        method: "POST",
        body: JSON.stringify({
          crop_variety_id: formVariety,
          growing_template_id: formTemplate,
          pasture_id: formPasture,
          field_subdivision_id: formField || null,
          sow_date: new Date(formSowDate).toISOString(),
          quantity_planted: formQuantity ? parseFloat(formQuantity) : null,
          quantity_unit: formQuantity ? formQuantityUnit : null,
          inventory_bindings: bindings,
        }),
      });
      toast.success("Planting created");
      resetForm();
      setDialogOpen(false);
      setLoading(true);
      fetchPlantings();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to create planting";
      toast.error(msg);
      console.error("[AddPlanting]", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Plantings"
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Plantings" }]}
      >
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
              <Plus className="h-4 w-4 mr-2" /> New Planting
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>New Planting</DialogTitle>
            </DialogHeader>
            {crops.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">
                You don't have any crops in your library yet.{" "}
                <Link to="/crops" className="text-ranch-green underline">
                  Clone a system crop
                </Link>{" "}
                to get started.
              </p>
            ) : (
              <form onSubmit={handleAdd} className="space-y-4">
                <div className="space-y-2">
                  <Label>Crop *</Label>
                  <Select value={formCrop} onValueChange={setFormCrop}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a crop" />
                    </SelectTrigger>
                    <SelectContent>
                      {crops.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {varieties.length > 0 && (
                  <div className="space-y-2">
                    <Label>Variety *</Label>
                    <Select value={formVariety} onValueChange={setFormVariety}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {varieties.map((v) => (
                          <SelectItem key={v.id} value={v.id}>
                            {v.name}
                            {v.days_to_maturity ? ` · ${v.days_to_maturity}d` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {templates.length > 0 && (
                  <div className="space-y-2">
                    <Label>Growing Template *</Label>
                    <Select value={formTemplate} onValueChange={setFormTemplate}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {templates.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name} ({t.cultivation_method.replace(/_/g, " ")})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Pasture *</Label>
                  <Select value={formPasture} onValueChange={setFormPasture}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a pasture" />
                    </SelectTrigger>
                    <SelectContent>
                      {pastures.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {fields.length > 0 && (
                  <div className="space-y-2">
                    <Label>Field (optional)</Label>
                    <Select value={formField || "_whole"} onValueChange={(v) => setFormField(v === "_whole" ? "" : v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_whole">Whole pasture</SelectItem>
                        {fields.map((f) => (
                          <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Sow Date *</Label>
                    <Input
                      type="date"
                      value={formSowDate}
                      onChange={(e) => setFormSowDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Quantity</Label>
                    <div className="flex gap-1">
                      <Input
                        type="number"
                        value={formQuantity}
                        onChange={(e) => setFormQuantity(e.target.value)}
                        placeholder="0"
                      />
                      <Select
                        value={formQuantityUnit}
                        onValueChange={(v) => setFormQuantityUnit(v as typeof formQuantityUnit)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="seeds">seeds</SelectItem>
                          <SelectItem value="transplants">transplants</SelectItem>
                          <SelectItem value="plants">plants</SelectItem>
                          <SelectItem value="lbs">lbs</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {templateInputs.length > 0 && (
                  <div className="space-y-2 border-t pt-4">
                    <Label className="text-sm font-medium">Inventory bindings</Label>
                    <p className="text-xs text-muted-foreground">
                      Pick the inventory item for each input the template requires.
                    </p>
                    {templateInputs.map((inp) => (
                      <div key={inp.id} className="grid grid-cols-2 gap-2 items-center">
                        <span className="text-sm">
                          {inp.item_name}
                          <span className="text-muted-foreground ml-1">({inp.unit})</span>
                        </span>
                        <Select
                          value={bindings[inp.id] ?? ""}
                          onValueChange={(v) => setBindings((b) => ({ ...b, [inp.id]: v }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Pick item..." />
                          </SelectTrigger>
                          <SelectContent>
                            {inventory.map((it) => (
                              <SelectItem key={it.id} value={it.id}>
                                {it.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                    {inventory.length === 0 && (
                      <p className="text-xs text-burnt-orange dark:text-burnt-orange-dark">
                        No inventory items found.{" "}
                        <Link to="/inventory" className="underline">
                          Add inventory
                        </Link>{" "}
                        before creating a planting.
                      </p>
                    )}
                  </div>
                )}

                <Button type="submit" disabled={submitting} className="w-full">
                  {submitting ? "Creating..." : "Create Planting"}
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="flex gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search plantings..."
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="planned">Planned</SelectItem>
            <SelectItem value="sown">Sown</SelectItem>
            <SelectItem value="growing">Growing</SelectItem>
            <SelectItem value="harvesting">Harvesting</SelectItem>
            <SelectItem value="complete">Complete</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <CardGridSkeleton count={6} />
      ) : error ? (
        <Card className="p-6 text-center text-clay dark:text-clay-dark">{error}</Card>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Sprout}
          title="No plantings"
          description={
            search || statusFilter !== "all"
              ? "Try adjusting your filters."
              : "Create your first planting to start tracking crops."
          }
          actionLabel={!search && statusFilter === "all" ? "New Planting" : undefined}
          onAction={!search && statusFilter === "all" ? () => setDialogOpen(true) : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <Link key={p.id} to={`/plantings/${p.id}`}>
              <Card className="p-5 hover:shadow-md transition-shadow cursor-pointer">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Sprout className="h-5 w-5 text-ranch-green" />
                    <h3 className="font-semibold text-foreground">{p.crop_name}</h3>
                  </div>
                  <Badge variant="outline" className={STATUS_STYLES[p.status]}>
                    {STATUS_LABELS[p.status]}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {p.variety_name}
                </p>
                <div className="text-sm mt-3 space-y-1">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>
                      {p.sow_date.split("T")[0]}
                      {p.expected_harvest_date && ` → ${p.expected_harvest_date.split("T")[0]}`}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {p.pasture_name}
                    {p.field_name && ` / ${p.field_name}`}
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
